import React, {useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomPanel, {atomPanelAlpha, atomPanelColors, atomPanelRounded} from '@s-ui/react-atom-panel'
import Context from '@s-ui/react-context'
import {titleBasicColors, titleBasicSizes, titleBasicTags, titleBasicTextAlign} from '@adv-mt/mt-title-basic'

import HeroBasicCTA, {heroBasicCTADesigns, heroBasicCTATypes} from './hero-basic-cta.js'
import HeroBasicExtraInfo from './hero-basic-extra-info.js'
import HeroBasicTag, {heroBasicTagSizes} from './hero-basic-tag.js'
import HeroBasicTitle from './hero-basic-title.js'

const BASE_CLASS = 'mt-HeroBasic'

const DEFAULT_BACKGROUND_COLOR = atomPanelColors.DARK
const DEFAULT_BACKGROUND_ALPHA = atomPanelAlpha.CONTRAST
const DEFAULT_BORDER_RADIUS = atomPanelRounded.L

const SIZES = {
  xl: 'xl',
  l: 'l',
  m: 'm',
  s: 's'
}

const BOTTOM_SPACE_SIZES = {
  m: 'm',
  l: 'l',
  xl: 'xl'
}

const OVERLAY_COLORS = {
  black: 'black',
  primary: 'primary'
}

const HeroBasic = ({
  backgroundColor,
  backgroundAlpha,
  bottomSpaceSize,
  children,
  cta,
  contentLeft,
  contentRight,
  contentCenter,
  extraInfo,
  fitToFullWidthLayout = false,
  imageSrc,
  overlayColor = OVERLAY_COLORS.black,
  panelContentComponent,
  panelRounded = DEFAULT_BORDER_RADIUS,
  size = SIZES.m,
  tag,
  title,
  topbar,
  withFullContrast,
  withLeftContrast,
  withRightContrast
}) => {
  // eslint-disable-next-line no-unused-vars
  const {i18n} = useContext(Context)

  const heroBasicClassName = cx(BASE_CLASS, {
    [`${BASE_CLASS}--withTopbar`]: topbar
  })
  const overlayClassName = cx(`${BASE_CLASS}-overlay`, {
    [`${BASE_CLASS}-overlay--withFullContrast--${overlayColor}`]: withFullContrast,
    [`${BASE_CLASS}-overlay--withLeftContrast`]: withLeftContrast,
    [`${BASE_CLASS}-overlay--withRightContrast`]: withRightContrast,
    [`${BASE_CLASS}-overlay--rounded`]: panelRounded !== atomPanelRounded.NONE
  })
  const topbarClassName = `${BASE_CLASS}-topbar`
  const panelClassName = cx(`${BASE_CLASS}-panel`, {
    [`${BASE_CLASS}-panel--withChildren`]: children,
    [`${BASE_CLASS}-panel--contentLeft`]: contentLeft,
    [`${BASE_CLASS}-panel--contentRight`]: contentRight,
    [`${BASE_CLASS}-panel--contentCenter`]: contentCenter,
    'is-fullWidthLayout': fitToFullWidthLayout
  })
  const panelContentClassName = cx(`${BASE_CLASS}-panelContent`, `${BASE_CLASS}-panelContent--${size}`, {
    [`${BASE_CLASS}-panelContent--${size}-bottomSpace--${bottomSpaceSize}`]: bottomSpaceSize
  })
  const panelContentCTAClassName = `${BASE_CLASS}-panelContentCTA`
  const headerClassName = `${BASE_CLASS}-panelHeader`
  const panelContentComponentClassName = `${BASE_CLASS}-panelContentComponent`
  const panelChildrenClassName = `${BASE_CLASS}-panelChildren`

  const atomPanelProps = {
    resized: true,
    rounded: panelRounded,
    ...(imageSrc && {
      src: imageSrc,
      overlayColor: 'CONTRAST',
      overlayAlpha: 'OVERLAY_D3'
    }),
    ...(!imageSrc && {color: backgroundColor || DEFAULT_BACKGROUND_COLOR}),
    ...(!imageSrc && {alpha: backgroundAlpha || DEFAULT_BACKGROUND_ALPHA})
  }

  return (
    <div className={heroBasicClassName}>
      <AtomPanel {...atomPanelProps}>
        <header className={overlayClassName}>
          {topbar && <div className={topbarClassName}>{topbar}</div>}
          <div className={panelClassName}>
            <div className={panelContentClassName}>
              {tag && (
                <div className={headerClassName}>
                  <HeroBasicTag tag={tag} />
                  {extraInfo && <HeroBasicExtraInfo {...extraInfo} />}
                </div>
              )}
              {title && <HeroBasicTitle title={title} />}
              {cta && (
                <div className={panelContentCTAClassName}>
                  <HeroBasicCTA cta={cta} />
                </div>
              )}
              {panelContentComponent && <div className={panelContentComponentClassName}>{panelContentComponent}</div>}
            </div>
            {children && <div className={panelChildrenClassName}>{children}</div>}
          </div>
        </header>
      </AtomPanel>
    </div>
  )
}

HeroBasic.displayName = 'HeroBasic'

HeroBasic.propTypes = {
  tag: PropTypes.shape({
    label: PropTypes.string,
    icon: PropTypes.node,
    size: PropTypes.oneOf(Object.values(heroBasicTagSizes))
  }),
  backgroundColor: PropTypes.oneOf(Object.values(atomPanelColors)),
  backgroundAlpha: PropTypes.oneOf(Object.values(atomPanelAlpha)),
  bottomSpaceSize: PropTypes.oneOf(Object.values(BOTTOM_SPACE_SIZES)),
  panelRounded: PropTypes.oneOf(Object.values(atomPanelRounded)),
  children: PropTypes.node,
  contentLeft: PropTypes.bool,
  contentRight: PropTypes.bool,
  contentCenter: PropTypes.bool,
  cta: PropTypes.shape({
    design: PropTypes.oneOf(Object.values(heroBasicCTADesigns)),
    onClick: PropTypes.func,
    text: PropTypes.string,
    type: PropTypes.oneOf(Object.values(heroBasicCTATypes)),
    url: PropTypes.string
  }),
  extraInfo: PropTypes.shape({
    label: PropTypes.string,
    icon: PropTypes.node
  }),
  fitToFullWidthLayout: PropTypes.bool,
  imageSrc: PropTypes.string,
  overlayColor: PropTypes.oneOf(Object.values(OVERLAY_COLORS)),
  panelContentComponent: PropTypes.node,
  size: PropTypes.oneOf(Object.values(SIZES)),
  title: PropTypes.shape({
    as: PropTypes.oneOf(Object.values(titleBasicTags)),
    children: PropTypes.node,
    color: PropTypes.oneOf(Object.values(titleBasicColors)),
    description: PropTypes.string,
    descriptionColor: PropTypes.oneOf(Object.values(titleBasicColors)),
    preTitle: PropTypes.string,
    size: PropTypes.oneOf(Object.values(titleBasicSizes)),
    title: PropTypes.string.isRequired
  }),
  topbar: PropTypes.node,
  /* Adds a dark overlay */
  withFullContrast: PropTypes.bool,
  withLeftContrast: PropTypes.bool,
  withRightContrast: PropTypes.bool
}

export {
  titleBasicTags as heroBasicTitleTags,
  titleBasicSizes as heroBasicTitleSizes,
  titleBasicColors as heroBasicTitleColors,
  titleBasicTextAlign as heroBasicTitleTextAlign,
  atomPanelColors as heroBasicPanelColors,
  atomPanelAlpha as heroBasicPanelAlphas,
  atomPanelRounded as heroBasicPanelRounded,
  BOTTOM_SPACE_SIZES as heroBasicBottomSpaceSizes,
  OVERLAY_COLORS as heroBasicOverlayColors,
  SIZES as heroBasicSizes,
  heroBasicCTADesigns,
  heroBasicCTATypes
}

export default HeroBasic
